/**
 * Messages service layer.
 * Abstracts all Firestore operations for direct messaging.
 * Supports real-time listeners via onSnapshot, typing indicators,
 * read receipts, and unread count tracking.
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  where,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const CONVERSATIONS_COLLECTION = 'conversations';
const MESSAGES_SUBCOLLECTION = 'messages';

/**
 * Generate a deterministic conversation ID from two user IDs.
 * Sorts alphabetically so the same pair always produces the same ID.
 */
const makeConversationId = (uid1, uid2) => {
  const sorted = [uid1, uid2].sort();
  return `${sorted[0]}_${sorted[1]}`;
};

/**
 * Serialize a Firestore conversation document.
 */
const serializeConversation = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    participants: data.participants || [],
    participantDetails: data.participantDetails || {},
    lastMessage: data.lastMessage
      ? {
          text: data.lastMessage.text || '',
          senderId: data.lastMessage.senderId || '',
          timestamp:
            data.lastMessage.timestamp instanceof Timestamp
              ? data.lastMessage.timestamp.toDate().toISOString()
              : data.lastMessage.timestamp || new Date().toISOString(),
        }
      : null,
    unreadCount: data.unreadCount || {},
    typing: data.typing || {},
    createdAt:
      data.createdAt instanceof Timestamp
        ? data.createdAt.toDate().toISOString()
        : data.createdAt || new Date().toISOString(),
    updatedAt:
      data.updatedAt instanceof Timestamp
        ? data.updatedAt.toDate().toISOString()
        : data.updatedAt || new Date().toISOString(),
  };
};

/**
 * Serialize a Firestore message document.
 */
const serializeMessage = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    senderId: data.senderId || '',
    text: data.text || '',
    imageUrl: data.imageUrl || null,
    readBy: data.readBy || [],
    reactions: data.reactions || {},
    replyTo: data.replyTo || null,
    createdAt:
      data.createdAt instanceof Timestamp
        ? data.createdAt.toDate().toISOString()
        : data.createdAt || new Date().toISOString(),
    type: data.type || 'text',
    _docSnapshot: docSnap,
  };
};

/**
 * Get or create a conversation between two users.
 * @param {string} userId1 - First user ID.
 * @param {string} userId2 - Second user ID.
 * @param {Object} user1Details - { displayName, username, avatarUrl } for user1.
 * @param {Object} user2Details - { displayName, username, avatarUrl } for user2.
 * @returns {Promise<string>} The conversation ID.
 */
export const getOrCreateConversation = async (userId1, userId2, user1Details, user2Details) => {
  try {
    const conversationId = makeConversationId(userId1, userId2);
    const convRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    const convSnap = await getDoc(convRef);

    if (convSnap.exists()) {
      return conversationId;
    }

    // Create new conversation
    await setDoc(convRef, {
      participants: [userId1, userId2],
      participantDetails: {
        [userId1]: {
          displayName: user1Details.displayName || '',
          username: user1Details.username || '',
          avatarUrl: user1Details.avatarUrl || '',
        },
        [userId2]: {
          displayName: user2Details.displayName || '',
          username: user2Details.username || '',
          avatarUrl: user2Details.avatarUrl || '',
        },
      },
      lastMessage: null,
      unreadCount: {
        [userId1]: 0,
        [userId2]: 0,
      },
      typing: {
        [userId1]: false,
        [userId2]: false,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Add system message
    const msgRef = doc(collection(convRef, MESSAGES_SUBCOLLECTION));
    await setDoc(msgRef, {
      senderId: 'system',
      text: 'Conversation started',
      imageUrl: null,
      readBy: [userId1, userId2],
      createdAt: serverTimestamp(),
      type: 'system',
    });

    return conversationId;
  } catch (error) {
    handleServiceError(error, 'Get or create conversation', 'Failed to start conversation. Please try again.');
  }
};

/**
 * Get all conversations for a user, ordered by last message timestamp.
 * @param {string} userId
 * @returns {Promise<Array<Object>>}
 */
export const getConversations = async (userId) => {
  try {
    const convsRef = collection(db, CONVERSATIONS_COLLECTION);
    const q = query(
      convsRef,
      where('participants', 'array-contains', userId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(serializeConversation);
  } catch (error) {
    handleServiceError(error, 'Get conversations', 'Failed to load conversations. Please try again.');
  }
};

/**
 * Real-time listener for conversation list.
 * @param {string} userId
 * @param {function} callback - Called with array of conversations.
 * @returns {function} Unsubscribe function.
 */
export const onConversationsChange = (userId, callback) => {
  const convsRef = collection(db, CONVERSATIONS_COLLECTION);
  const q = query(
    convsRef,
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const conversations = snapshot.docs.map(serializeConversation);
      callback(conversations);
    },
    (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Conversations listener]', error);
      }
      callback([]);
    }
  );
};

/**
 * Send a message in a conversation.
 * @param {string} conversationId
 * @param {string} senderId
 * @param {string} text
 * @param {string|null} [imageUrl]
 * @returns {Promise<void>}
 */
export const sendMessage = async (conversationId, senderId, text, imageUrl = null, replyTo = null) => {
  try {
    const convRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    const convSnap = await getDoc(convRef);

    if (!convSnap.exists()) {
      throw new Error('Conversation not found.');
    }

    const convData = convSnap.data();
    const otherUserId = convData.participants.find((p) => p !== senderId);

    // Add message
    const msgRef = doc(collection(convRef, MESSAGES_SUBCOLLECTION));
    const messageType = imageUrl ? 'image' : 'text';
    const messageText = imageUrl && !text ? 'Sent an image' : text;

    const messageData = {
      senderId,
      text: messageText,
      imageUrl: imageUrl || null,
      readBy: [senderId],
      createdAt: serverTimestamp(),
      type: messageType,
    };

    if (replyTo) {
      messageData.replyTo = {
        messageId: replyTo.messageId || '',
        text: replyTo.text || '',
        senderName: replyTo.senderName || '',
      };
    }

    await setDoc(msgRef, messageData);

    // Update conversation last message and unread count
    const currentUnread = convData.unreadCount?.[otherUserId] || 0;
    await updateDoc(convRef, {
      lastMessage: {
        text: messageText,
        senderId,
        timestamp: serverTimestamp(),
      },
      [`unreadCount.${otherUserId}`]: currentUnread + 1,
      [`typing.${senderId}`]: false,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    handleServiceError(error, 'Send message', 'Failed to send message. Please try again.');
  }
};

/**
 * Get paginated message history for a conversation.
 * @param {string} conversationId
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc
 * @param {number} [pageLimit=50]
 * @returns {Promise<{messages: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getMessages = async (conversationId, lastDoc = null, pageLimit = 50) => {
  try {
    const msgsRef = collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_SUBCOLLECTION);
    let q;

    if (lastDoc) {
      q = query(
        msgsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        msgsRef,
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit)
      );
    }

    const snapshot = await getDocs(q);
    const messages = snapshot.docs.map(serializeMessage).reverse();
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    const hasMore = snapshot.docs.length === pageLimit;

    return { messages, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get messages', 'Failed to load messages. Please try again.');
  }
};

/**
 * Real-time listener for new messages in a conversation.
 * @param {string} conversationId
 * @param {function} callback - Called with array of messages.
 * @returns {function} Unsubscribe function.
 */
export const onMessagesChange = (conversationId, callback) => {
  const msgsRef = collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_SUBCOLLECTION);
  const q = query(msgsRef, orderBy('createdAt', 'asc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs.map(serializeMessage);
      callback(messages);
    },
    (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Messages listener]', error);
      }
      callback([]);
    }
  );
};

/**
 * Mark a conversation as read for a user.
 * @param {string} conversationId
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const markConversationRead = async (conversationId, userId) => {
  try {
    const convRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);

    // Reset unread count
    await updateDoc(convRef, {
      [`unreadCount.${userId}`]: 0,
    });

    // Mark all unread messages as read by this user
    const msgsRef = collection(convRef, MESSAGES_SUBCOLLECTION);
    const snapshot = await getDocs(msgsRef);
    const batch = writeBatch(db);
    let batchCount = 0;

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (!data.readBy || !data.readBy.includes(userId)) {
        batch.update(docSnap.ref, { readBy: arrayUnion(userId) });
        batchCount++;
      }
    });

    if (batchCount > 0) {
      await batch.commit();
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Mark conversation read]', error);
    }
  }
};

/**
 * Set typing indicator for a user in a conversation.
 * @param {string} conversationId
 * @param {string} userId
 * @param {boolean} isTyping
 * @returns {Promise<void>}
 */
export const setTyping = async (conversationId, userId, isTyping) => {
  try {
    const convRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    await updateDoc(convRef, {
      [`typing.${userId}`]: isTyping,
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Set typing]', error);
    }
  }
};

/**
 * Listen to typing state changes in a conversation.
 * @param {string} conversationId
 * @param {function} callback - Called with typing object.
 * @returns {function} Unsubscribe function.
 */
export const onTypingChange = (conversationId, callback) => {
  const convRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);

  return onSnapshot(
    convRef,
    (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data().typing || {});
      }
    },
    (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Typing listener]', error);
      }
      callback({});
    }
  );
};

/**
 * Get total unread message count across all conversations for a user.
 * @param {string} userId
 * @returns {Promise<number>}
 */
export const getTotalUnreadCount = async (userId) => {
  try {
    const convsRef = collection(db, CONVERSATIONS_COLLECTION);
    const q = query(
      convsRef,
      where('participants', 'array-contains', userId)
    );
    const snapshot = await getDocs(q);
    let total = 0;
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      total += data.unreadCount?.[userId] || 0;
    });
    return total;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Get total unread]', error);
    }
    return 0;
  }
};

/**
 * Real-time listener for total unread message count.
 * @param {string} userId
 * @param {function} callback - Called with unread count number.
 * @returns {function} Unsubscribe function.
 */
export const onTotalUnreadChange = (userId, callback) => {
  const convsRef = collection(db, CONVERSATIONS_COLLECTION);
  const q = query(
    convsRef,
    where('participants', 'array-contains', userId)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      let total = 0;
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        total += data.unreadCount?.[userId] || 0;
      });
      callback(total);
    },
    (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Total unread listener]', error);
      }
      callback(0);
    }
  );
};

/**
 * Soft delete a conversation for a user (hides from their list).
 * @param {string} conversationId
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const deleteConversation = async (conversationId, userId) => {
  try {
    const convRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    await updateDoc(convRef, {
      [`deletedBy.${userId}`]: true,
    });
  } catch (error) {
    handleServiceError(error, 'Delete conversation', 'Failed to delete conversation. Please try again.');
  }
};

/**
 * Add an emoji reaction to a message.
 * Uses Firestore arrayUnion to add the userId to the reaction's array.
 * @param {string} conversationId
 * @param {string} messageId
 * @param {string} emoji - The emoji character.
 * @param {string} userId - The user adding the reaction.
 * @returns {Promise<void>}
 */
export const addReaction = async (conversationId, messageId, emoji, userId) => {
  try {
    const msgRef = doc(
      db,
      CONVERSATIONS_COLLECTION,
      conversationId,
      MESSAGES_SUBCOLLECTION,
      messageId
    );
    await updateDoc(msgRef, {
      [`reactions.${emoji}`]: arrayUnion(userId),
    });
  } catch (error) {
    handleServiceError(error, 'Add reaction', 'Failed to add reaction. Please try again.');
  }
};

/**
 * Remove an emoji reaction from a message.
 * Uses Firestore arrayRemove to remove the userId from the reaction's array.
 * @param {string} conversationId
 * @param {string} messageId
 * @param {string} emoji - The emoji character.
 * @param {string} userId - The user removing the reaction.
 * @returns {Promise<void>}
 */
export const removeReaction = async (conversationId, messageId, emoji, userId) => {
  try {
    const msgRef = doc(
      db,
      CONVERSATIONS_COLLECTION,
      conversationId,
      MESSAGES_SUBCOLLECTION,
      messageId
    );
    await updateDoc(msgRef, {
      [`reactions.${emoji}`]: arrayRemove(userId),
    });
  } catch (error) {
    handleServiceError(error, 'Remove reaction', 'Failed to remove reaction. Please try again.');
  }
};

/**
 * Upload a chat image to Firebase Storage.
 * Uses the existing storage service pattern.
 * @param {File} file
 * @param {string} conversationId
 * @param {function} [onProgress]
 * @returns {Promise<string>} Download URL.
 */
export const uploadChatImage = async (file, conversationId, onProgress) => {
  // Delegate to storage service - imported dynamically to avoid circular deps
  const { uploadPostImage } = await import('./storage.service');
  return uploadPostImage(file, `messages/${conversationId}`, onProgress);
};
